import { useContext, useEffect, useRef } from 'react';
import AppContext from '../../contexts/AppContext';
import { AppDivisionContext } from '../../contexts/AppDivisionContext';
import { getRecentActivity } from '../../utils/activityBuffer';
import { fitToBudget } from '../../utils/feedbackPayload';
import { env } from '../../config';

/**
 * SW2-2394 — attaches context and a recent-activity timeline to every Usersnap
 * report.
 *
 * Renders nothing. Usersnap shows `custom` data under **Additional
 * Information**, and the Slack integration reproduces that block inline — which
 * is what makes it readable by an automated triage reader. The console log and
 * screenshot Usersnap also captures are only reachable through an
 * authenticated link, so anything triage must see has to travel this way.
 *
 * Must live *inside* the provider tree rather than beside the Usersnap block in
 * `App.tsx`, because `App` renders `AppStore` and `AppDivisionProvider` and so
 * cannot consume them itself.
 *
 * Values are read when the widget opens rather than at load, via
 * `api.on('open')`. Attaching at `init` would freeze whatever was true when the
 * app booted: the division and — most importantly — the activity list would
 * both be empty, since nothing has happened yet.
 *
 * Every key here competes for space: Usersnap sorts custom data keys
 * alphabetically and the Slack post truncates. See PAYLOAD_BUDGET below for
 * what the limit actually applies to.
 *
 * Truncation takes the tail, so `timeline` is named to sort *last* and is
 * ordered newest-first. A long timeline then costs its own oldest entries
 * rather than costing the context that says who and where — and the newest
 * event, the one that usually explains the report, survives either way. The
 * scalars above it are the facts the Slack post does not already carry: Sender
 * and Referer give the reporter and the route, but nothing else gives the
 * division name, the role, or the clock.
 *
 * Uninformative fields are omitted rather than sent empty. Keep all of that
 * true when adding anything.
 *
 * There is deliberately no service-worker field. The question it would answer —
 * is this tab running superseded code — cannot be answered from the
 * registration API here: `service-worker.js` calls both `skipWaiting()` and
 * `clientsClaim()`, so a replaced worker never sits in `waiting` and the
 * controller is never left pointing at an older worker. `build` answers it
 * better anyway, by naming the exact commit the bundle came from.
 */

/**
 * Fired by `App.tsx` once the Usersnap api object exists, so the open handler is
 * attached without waiting for the next poll tick.
 */
export const USERSNAP_READY_EVENT = 'sw2:usersnap-ready';

/** How long to wait for the Usersnap script, which loads async and may be off. */
const WAIT_TIMEOUT_MS = 30000;
const WAIT_INTERVAL_MS = 500;

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/**
 * The reporter's wall clock and UTC offset, as "2026-09-23 14:25 -04:00".
 *
 * Worth its line because this domain is full of date-boundary bugs: rain totals
 * are bucketed by UTC day, inspections fall due on a local one, and a report
 * like "today it only updated until 11am" cannot be read without knowing which
 * day the reporter meant. The offset answers that.
 *
 * No seconds and no IANA zone name: the zone costs about eighteen characters to
 * restate what the offset already says, and only pays for itself across a
 * daylight-saving transition. Characters here come straight out of the
 * timeline.
 */
function describeLocalTime(now: Date = new Date()): string {
  // getTimezoneOffset counts minutes *west* of UTC, so negate it to read the
  // way offsets are normally written.
  const offsetMinutes = -now.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absolute = Math.abs(offsetMinutes);
  const offset = `${sign}${pad(Math.floor(absolute / 60))}:${pad(
    absolute % 60,
  )}`;

  // sv-SE formats as YYYY-MM-DD HH:mm:ss, unambiguous in a way the browser's
  // own locale is not; sliced to the minute.
  return `${now.toLocaleString('sv-SE').slice(0, 16)} ${offset}`;
}

/**
 * The division as "145 (Build Site Solutions)".
 *
 * Both facts in one field rather than two, because a second key costs a line
 * for no extra meaning. The id is what a query needs; the name is what makes
 * the report readable to someone who has not memorised the ids.
 *
 * Falls back to the bare id when the division list has not loaded or holds no
 * match, which is normal early in a session.
 */
function describeDivision(
  divisionId: number | null,
  divisions: { id: number; name: string }[],
): string {
  // Zero as well as null: AppDivisionContext seeds this with
  // `Number(localStorage.getItem('divisionId'))`, and `Number(null)` is 0. On a
  // fresh session the id is 0, not null, and without this the report reads
  // "division 0", sending a triager after a division that does not exist.
  if (divisionId == null || divisionId === 0) {
    return 'none';
  }

  const match = (divisions || []).find(
    (division) => division.id === divisionId,
  );

  return match ? `${divisionId} (${match.name})` : String(divisionId);
}

/**
 * A stale service worker is a classic cause of "broken for me, fine for
 * everyone else", and it is invisible from a screenshot. `waiting` means an
 * update has downloaded but the old worker is still serving the page, which is
 * the state worth knowing about.
 */
const UsersnapCustomData = () => {
  const appStore = useContext(AppContext);
  // Read defensively rather than destructured. This also mounts inside
  // `LockedTrialGate`, which `AppStore` renders *instead of* its children, so
  // there is no provider above it there and both contexts are undefined. A
  // locked user gets a degraded report — no division or role — rather than the
  // empty one they got before, and the timeline is the part that matters.
  const divisionContext: any = useContext(AppDivisionContext) || {};
  const appDivisionId = divisionContext.appDivisionId ?? null;
  const divisions = divisionContext.divisions || [];

  // The 'open' handler is registered once, but must report what is true *now*.
  // A ref keeps the latest values reachable without re-registering the handler
  // on every context change, which would stack duplicates.
  const latest = useRef({ appStore, appDivisionId, divisions });

  latest.current = { appStore, appDivisionId, divisions };

  useEffect(() => {
    let cancelled = false;
    let waited = 0;
    let timer: ReturnType<typeof setInterval>;
    // Held so the handler can be removed on unmount. An orphaned one keeps
    // answering from a `latest` ref that stopped updating, and since both would
    // write the same `custom` key, a stale reply could win — putting a wrong
    // but plausible division and role in front of whoever triages.
    let attached: any = null;

    const onOpen = (event: any) => {
        try {
        const {
          appStore: store,
          appDivisionId: divisionId,
          divisions: divisionList,
        } = latest.current;
        const user = (store?.get('user') as Record<string, any>) || {};

        const payload: Record<string, any> = {
          // Who, as one line. The tenant and the route are deliberately absent:
          // the Slack post's own Referer already carries both, and a repeated
          // fact costs the timeline an event.
          //
          // The role is kept because a fair share of reports are
          // permission-shaped — "inspection not found" means access resolution
          // failed, not that the record is missing — and nothing else in the
          // post carries it. The Slack header gives an email, not a role.
          context: [
            `division ${describeDivision(divisionId, divisionList)}`,
            `user ${user.id != null ? user.id : 'unknown'} (${
              user.roleName || 'unknown role'
            })`,
          ].join(' · '),

          localTime: describeLocalTime(),
        };

        // Which build produced the report, so a bug can be tied to the code
        // that caused it rather than to whatever is deployed by the time anyone
        // looks. CI already writes this into .env at build time
        // (.github/workflows/ci-cd.yml, "Append CI metadata"); it is simply not
        // read anywhere yet.
        //
        // Short form: the full SHA is forty characters, a whole timeline entry's
        // worth of budget, and seven is what everyone pastes into git anyway.
        // Absent in a local build, and omitted rather than sent empty, because a
        // line that always says the same thing costs the timeline an event.
        const commit = env.REACT_APP_CI_COMMIT_SHA;

        if (commit) {
          payload.build = String(commit).slice(0, 7);
        }

        // `timeline` is added here, trimmed to what the block can carry. It is
        // named to sort last so that if the budget above is still too generous,
        // the tail Slack cuts is the oldest events rather than the context.
        if (typeof event?.api?.setValue === 'function') {
          event.api.setValue('custom', fitToBudget(payload, getRecentActivity()));
        }
        } catch (error) {
          // The one place where "must never break the host app" is
          // load-bearing: this runs inside Usersnap's own event dispatch, so a
          // throw could leave the widget half-opened and the user unable to
          // file the report at all. Logged rather than swallowed, because
          // nothing else distinguishes "working" from "silently dead".
          // eslint-disable-next-line no-console
          console.warn('SW2-2394: could not attach custom data', error);
        }
    };

    const attach = (api: any) => {
      // Idempotent: the ready event and the poller below can both fire, and a
      // second registration would mean two handlers writing the same key.
      if (attached || typeof api?.on !== 'function') {
        return;
      }

      attached = api;
      api.on('open', onOpen);
    };

    // App.tsx fires this the moment it has the api, which closes the window
    // between the widget becoming clickable and the handler being attached. A
    // report opened in that window would carry no custom data, and the open
    // event is not replayed.
    const onUsersnapReady = () => {
      if (!cancelled) {
        attach((window as any).Usersnap);
      }
    };

    window.addEventListener(USERSNAP_READY_EVENT, onUsersnapReady);

    // Already loaded before this mounted — the common case on a route change.
    attach((window as any).Usersnap);

    // Retained as a fallback in case the event is missed entirely.
    timer = setInterval(() => {
      const api = (window as any).Usersnap;

      if (api) {
        clearInterval(timer);

        if (!cancelled) {
          attach(api);
        }

        return;
      }

      waited += WAIT_INTERVAL_MS;

      // The widget is behind a feature flag and is off in most environments.
      // Give up quietly rather than polling for the life of the session.
      if (waited >= WAIT_TIMEOUT_MS) {
        clearInterval(timer);
      }
    }, WAIT_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener(USERSNAP_READY_EVENT, onUsersnapReady);

      if (typeof attached?.off === 'function') {
        attached.off('open', onOpen);
      }

      attached = null;
    };
  }, []);

  return null;
};

export default UsersnapCustomData;
